import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Ruler } from 'lucide-react';
import CircleControl from './shared/CircleControl';
import { useScaleBar } from '../hooks/useScaleBar';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import { computeNiceScale } from '../utils/scaleBar';
const MAX_BAR_PX = 120;
const COLLAPSED_STORAGE_KEY = 'landscope_scalebar_collapsed';
const readCollapsed = () => {
    try {
        return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
    }
    catch (_a) {
        return false;
    }
};
const writeCollapsed = (collapsed) => {
    try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    }
    catch (_a) {
        // localStorage unavailable (private mode / quota) — preference just won't persist.
    }
};
// Always-on cartographic scale bar. Reads the live ground resolution from
// useScaleBar and the app-wide unit system from useUnitPreferences, then snaps
// the bar to a clean 1 / 2 / 5 round distance. Shares the forest-glass +
// pewter-border family used by the compass / legend / map controls. When
// `inline`, it drops its own positioning so a parent can place it in the
// bottom-right cartographic cluster. Click to collapse it to a ruler icon;
// click the icon to bring it back.
export const ScaleBar = ({ viewer, inline = false }) => {
    const metersPerPixel = useScaleBar(viewer);
    const { system } = useUnitPreferences();
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const updateCollapsed = (next) => {
        setCollapsed(next);
        writeCollapsed(next);
    };
    const nice = useMemo(() => (metersPerPixel ? computeNiceScale(metersPerPixel, MAX_BAR_PX, system) : null), [metersPerPixel, system]);
    if (!viewer)
        return null;
    const glass = { backgroundColor: 'var(--forest)', borderColor: 'var(--pewter)' };
    const posClass = inline ? '' : 'fixed bottom-7 left-2 z-30 ';
    // Collapsed → a ruler-icon puck that hover-expands to a "Scale" label, matching
    // the top-right pucks; click reopens the live scale bar.
    if (collapsed) {
        return (_jsx(CircleControl, { icon: _jsx(Ruler, { size: 18 }), label: "Scale", onClick: () => updateCollapsed(false), title: "Show scale bar", className: inline ? '' : 'fixed bottom-7 left-2 z-30' }));
    }
    // Expanded but no valid reading (e.g. camera looking at the horizon/sky).
    if (!nice)
        return null;
    return (_jsxs("button", { type: "button", onClick: () => updateCollapsed(true), title: "Hide scale bar", "aria-label": "Hide scale bar", className: `${posClass}pointer-events-auto select-none cursor-pointer rounded-md border px-2 py-1 shadow-lg backdrop-blur-sm`, style: glass, children: [_jsxs("div", { className: "relative", style: { width: Math.round(nice.widthPx), height: 8 }, children: [_jsx("div", { className: "absolute left-0 right-0 bottom-0 h-[2px]", style: { backgroundColor: 'var(--sand)' } }), _jsx("div", { className: "absolute left-0 bottom-0 w-[1px] h-[8px]", style: { backgroundColor: 'var(--sand)' } }), _jsx("div", { className: "absolute right-0 bottom-0 w-[1px] h-[8px]", style: { backgroundColor: 'var(--sand)' } })] }), _jsx("div", { className: "mt-[3px] text-center text-[10px] font-semibold leading-none", style: { color: 'var(--sand)' }, children: nice.label })] }));
};
export default ScaleBar;
